// Gesto de voltar puxando da borda esquerda (0.70, texto 09, Parte E).
//
// CAUSA de o gesto do iPhone não funcionar em nenhuma tela: quase todas as "telas" do app não são telas de uma pilha
// de navegação. As abas usam Tabs (sem pilha: aba/[id] é uma aba escondida), as subpáginas (Ajustes, itens dos módulos,
// partes do Diário) são trocas de estado dentro da mesma tela, e as folhas (matéria, página, editar...) são janelas
// <Modal presentationStyle="fullScreen">. O gesto nativo do iPhone só existe numa pilha nativa (Stack), por isso
// não havia o que puxar.
//
// Este componente dá o gesto a qualquer tela: uma faixa fina na borda esquerda (só no iPhone) pega o dedo,
// a tela acompanha o dedo e, passando de ~35% da largura (ou num puxão rápido), desliza para fora e chama o
// "voltar" que a tela já tem (o mesmo do "‹" / "Fechar"). Se soltar antes, a tela volta para o lugar.
// A faixa é uma view de verdade por cima da tela: só recebe o dedo quem está VISÍVEL no topo (a janela de cima
// cobre as de baixo), então telas empilhadas não brigam pelo gesto.
// Android: tem o "voltar" do sistema (BackHandler / onRequestClose); a faixa não aparece.
//
// 0.72 (texto 11a, A) — CAUSA da TELA BRANCA: ao completar o gesto, a tela desliza para fora (translateX = largura)
// e só voltava para o lugar se `ativo` continuasse true. Nos Ajustes, `ativo` era "tem subpágina aberta": voltar de
// "Atualizações e sobre" (ou de qualquer subpágina) para a capa deixava `ativo` = false, então a tela INTEIRA dos Ajustes
// ficava parada fora da tela (branca, só a barra de baixo e o +), e continuava branca toda vez que se voltava aos Ajustes
// (inclusive pelo "voltar" de Estudos, que ia para a aba anterior do histórico). Agora:
//  - depois de um gesto completo a tela SEMPRE volta para o lugar se continuar montada; só uma JANELA (Sheet, `janela`)
//    que fechou fica fora até abrir de novo (para não reaparecer durante a animação de fechar);
//  - a animação é do lado JS (useNativeDriver: false): qualquer nova renderização grava a posição atual, então uma aba
//    escondida (react-native-screens) não volta "presa" fora da tela;
//  - se o "voltar" da tela der erro, cai no My Planner (proteção geral).
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Animated, Dimensions, Easing, PanResponder, Platform, View, type ViewStyle } from 'react-native';
import { voltarAoMyPlanner } from '../lib/voltarPlanner';

const BORDA = 16;          // largura da faixa que pega o dedo (o conteúdo das telas começa em 16)
const PASSA = 0.35;        // passou de 35% da largura: volta
const RAPIDO = 0.5;        // ou puxão rápido (px/ms)

/** Bloqueios do gesto vindos de dentro da tela (ex.: caneta, borracha ou marca-texto escolhidos no quadro). */
const Ctx = createContext<((on: boolean) => () => void) | null>(null);

/** Desliga o gesto da borda da tela em que este componente está enquanto `on` for true. */
export function useBloquearVoltarBorda(on: boolean) {
  const bloquear = useContext(Ctx);
  useEffect(() => (on && bloquear ? bloquear(true) : undefined), [on, bloquear]);
}

/** `janela`: é uma janela (Sheet/Modal) que some quando `ativo` vira false. Fora isso, `ativo` só liga/desliga o gesto. */
export function VoltarBorda({ onVoltar, ativo = true, janela = false, children, style }:
  { onVoltar?: () => void; ativo?: boolean; janela?: boolean; children: ReactNode; style?: ViewStyle }) {
  const x = useRef(new Animated.Value(0)).current;
  const op = useRef(new Animated.Value(1)).current;
  const [bloqueios, setBloqueios] = useState(0);
  const bloquear = useMemo(() => (_on: boolean) => {
    setBloqueios(b => b + 1);
    let feito = false;
    return () => { if (feito) return; feito = true; setBloqueios(b => Math.max(0, b - 1)); };
  }, []);
  const ref = useRef({ onVoltar, ativo, janela });
  ref.current = { onVoltar, ativo, janela };
  const vivo = useRef(true);
  useEffect(() => () => { vivo.current = false; }, []);
  // voltou a ficar ativa (janela aberta de novo) ou a tela desligou o gesto sem sair: começa no lugar.
  // Só uma janela que fechou (janela && !ativo) pode ficar fora.
  useEffect(() => { if (ativo || !janela) { x.setValue(0); op.setValue(1); } }, [ativo, janela, x, op]);

  const pan = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderTerminationRequest: () => false,
    onPanResponderMove: (_, g) => x.setValue(Math.max(0, g.dx)),
    onPanResponderRelease: (_, g) => {
      const W = Dimensions.get('window').width;
      if (g.dx > 20 && (g.dx > W * PASSA || g.vx > RAPIDO)) {
        Animated.timing(x, { toValue: W, duration: Math.max(120, Math.min(260, ((W - g.dx) / Math.max(1, g.vx)) || 220)),
          easing: Easing.out(Easing.cubic), useNativeDriver: false }).start(() => {
          // proteção geral (texto 11a, A3): se o "voltar" da tela falhar, vai para o My Planner
          try { const f = ref.current.onVoltar; if (f) f(); else voltarAoMyPlanner(); } catch { voltarAoMyPlanner(); }
          // A tela continua montada (subpágina que trocou de conteúdo, raiz da aba que foi para o My Planner, janela que
          // perguntou algo): SEMPRE volta para o lugar. Só a janela que fechou fica fora até abrir de novo.
          setTimeout(() => {
            if (!vivo.current) return;
            if (ref.current.janela && !ref.current.ativo) return;
            x.setValue(0); op.setValue(0);
            Animated.timing(op, { toValue: 1, duration: 140, useNativeDriver: false }).start();
          }, 80);
        });
      } else Animated.spring(x, { toValue: 0, useNativeDriver: false, bounciness: 0, speed: 18 }).start();
    },
    onPanResponderTerminate: () => Animated.spring(x, { toValue: 0, useNativeDriver: false, bounciness: 0, speed: 18 }).start(),
  }), [x, op]);

  const W = Dimensions.get('window').width;
  const comGesto = Platform.OS === 'ios' && ativo && !!onVoltar && bloqueios === 0;
  return (
    <Ctx.Provider value={bloquear}>
      <View style={[{ flex: 1 }, style]}>
        {Platform.OS === 'ios' && (
          // sombra leve atrás da tela enquanto ela é puxada (some quando a tela está no lugar)
          <Animated.View pointerEvents="none" style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: '#000',
            opacity: x.interpolate({ inputRange: [0, 1, W], outputRange: [0, 0.18, 0], extrapolate: 'clamp' }) }} />
        )}
        <Animated.View style={{ flex: 1, opacity: op, transform: [{ translateX: x }] }}>
          {children}
          {Platform.OS === 'ios' && (
            <Animated.View pointerEvents="none" style={{ position: 'absolute', top: 0, bottom: 0, left: -6, width: 6, backgroundColor: '#000',
              opacity: x.interpolate({ inputRange: [0, 1], outputRange: [0, 0.12], extrapolate: 'clamp' }) }} />
          )}
        </Animated.View>
        {comGesto && <View {...pan.panHandlers} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: BORDA, zIndex: 9999 }} />}
      </View>
    </Ctx.Provider>
  );
}
